import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useBrandManager } from './useBrandManager';
import { useEffect } from 'react';
import { usePageVisibility, useAdaptiveRefetchInterval } from './usePageVisibility';

/**
 * Hook to get the count of unread support messages for the current user
 * A message is considered unread if it:
 * 1. Has no read_at timestamp
 * 2. Was NOT sent by the current user
 * 3. Belongs to a thread in the current brand
 */
export const useSupportUnreadCount = () => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const { isVisible } = usePageVisibility();
  
  // Only refetch when tab is visible
  const refetchInterval = useAdaptiveRefetchInterval(60000, false); // 60s when visible, disabled when hidden

  const { data: unreadCount = 0, refetch } = useQuery({
    queryKey: ['support-unread-count', user?.id, currentBrandId],
    queryFn: async () => {
      if (!user?.id || !currentBrandId) return 0;

      // Get all threads for the current brand
      const { data: threads, error: threadsError } = await supabase
        .from('support_threads')
        .select('id')
        .eq('brand_id', currentBrandId);

      if (threadsError || !threads || threads.length === 0) return 0;

      const threadIds = threads.map(t => t.id);

      // Count unread messages in those threads that were not sent by current user
      const { count, error } = await supabase
        .from('support_messages')
        .select('id', { count: 'exact', head: true })
        .in('thread_id', threadIds)
        .is('read_at', null)
        .neq('user_id', user.id);

      if (error) {
        console.error('Error fetching unread count:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!user?.id && !!currentBrandId,
    refetchInterval, // Adaptive based on visibility
  });

  // Set up real-time subscription for messages (visibility-aware)
  useEffect(() => {
    if (!currentBrandId || !user?.id || !isVisible) return;

    const channel = supabase
      .channel(`support-unread-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_messages',
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentBrandId, user?.id, refetch, isVisible]);

  return unreadCount;
};
